/**
 * Global auto-call-transfer configuration loader (Ozonetel, Cube, Centro).
 *
 * Single source of truth: auto-call-transfer-config.json
 * Each dialer section holds campaigns with dummyUsers (source agents) and activeUsers (destination agents).
 * JSON entries use { moneytorUser, dialerUser } objects — only dialerUser is used at runtime.
 *
 * Flow:
 * 1) App boot → dialer modules (ozonetel-auto-transfer-config.ts, cube-auto-transfer-config.ts) import this file.
 * 2) initAutoCallTransferConfig() runs:
 *    - AUTO_CALL_TRANSFER_USE_S3 = false → use bundled JSON immediately.
 *    - AUTO_CALL_TRANSFER_USE_S3 = true  → fetch unified JSON from S3, then notify subscribers.
 * 3) Dialer modules call getOzonetelCampaignsConfig() / getCubeCampaignsConfig() → applyConfig() in their own files.
 * 4) calling.component uses dialer-specific helpers (isDummyAgent, pickTarget, etc.) — transfer logic stays in dialer files.
 *
 * JSON shape:
 * {
 *   "ozonetel": { "campaigns": { "care_calling": { "dummyUsers": [...], "activeUsers": [...] } } },
 *   "cube":     { "campaigns": { "Moneytor_CareCalling": { "dummyUsers": [...], "activeUsers": [...] } } }
 * }
 */
import bundledConfig from './auto-call-transfer-config.json';

/** false = bundled JSON (default); true = fetch from AUTO_CALL_TRANSFER_S3_URL at runtime. */
export const AUTO_CALL_TRANSFER_USE_S3 = true;

/** Public S3 URL for the unified auto-call-transfer JSON (used when AUTO_CALL_TRANSFER_USE_S3 is true). */
export const AUTO_CALL_TRANSFER_S3_URL =
  'https://moneytor-uploads.s3.ap-south-1.amazonaws.com/developer/portfolio-zestmoneyJSONData/auto-call-transfer-config.json';

/** One user entry in JSON — moneytorUser is for identification only; dialerUser drives transfer logic. */
export interface DialerUserEntry {
  moneytorUser?: string;
  dialerUser?: string;
}

/** Normalized per-campaign user lists returned to Ozonetel (same-campaign warm transfer). */
export interface CampaignUserLists {
  dummyUsers: string[];
  activeUsers: string[];
}

/** Normalized per-campaign lists for Cube; targetCampaign defaults to the campaign key (same-campaign transfer). */
export interface CubeCampaignUserLists extends CampaignUserLists {
  targetCampaign: string;
}

/** One dialer's campaigns block inside the unified JSON (ozonetel | cube | centro). */
export interface DialerCampaignsSection {
  campaigns?: Record<
    string,
    {
      dummyUsers?: unknown;
      activeUsers?: unknown;
      targetCampaign?: string;
    }
  >;
}

/** Root shape of auto-call-transfer-config.json. */
export interface AutoCallTransferConfig {
  ozonetel?: DialerCampaignsSection;
  cube?: DialerCampaignsSection;
  centro?: DialerCampaignsSection;
}

type ConfigReadyCallback = () => void;

/** In-memory config: bundled JSON by default; replaced after successful S3 fetch when flag is true. */
let activeConfig: AutoCallTransferConfig = bundledConfig as AutoCallTransferConfig;

/** Subscribers notified after config is ready (immediate for local JSON, after fetch for S3). */
const readyCallbacks: ConfigReadyCallback[] = [];

/**
 * Case-insensitive id comparison helper for deduplicating dialerUser values.
 */
function normalizeId(value: string): string {
  return String(value ?? '').trim().toLowerCase();
}

/**
 * Converts JSON dummyUsers / activeUsers into a deduplicated dialerUser string array.
 *
 * Supports:
 * - Object entries: { dialerUser: "Agent7" } — moneytorUser is ignored.
 * - Legacy plain strings: "Agent7" or ["Agent6", "Agent5"].
 * - Comma-separated strings inside array entries: ["Agent6, Agent5"].
 *
 * Used by buildOzonetelCampaigns / buildCubeCampaigns and re-exported to dialer config modules.
 */
export function extractDialerUsers(value: unknown): string[] {
  if (value == null) {
    return [];
  }
  const items = Array.isArray(value) ? value : [value];
  const out: string[] = [];
  for (const item of items) {
    if (item != null && typeof item === 'object' && 'dialerUser' in item) {
      const id = String((item as DialerUserEntry).dialerUser ?? '').trim();
      if (id && !out.some((existing) => normalizeId(existing) === normalizeId(id))) {
        out.push(id);
      }
      continue;
    }
    const raw = String(item ?? '').trim();
    if (!raw) {
      continue;
    }
    const parts = raw.includes(',') ? raw.split(',') : [raw];
    for (const part of parts) {
      const id = String(part ?? '').trim();
      if (id && !out.some((existing) => normalizeId(existing) === normalizeId(id))) {
        out.push(id);
      }
    }
  }
  return out;
}

/**
 * Builds Ozonetel campaign map from the ozonetel section of activeConfig.
 * Each campaign key is the target campaign; transfer stays within that campaign.
 */
function buildOzonetelCampaigns(
  section: DialerCampaignsSection | undefined
): Record<string, CampaignUserLists> {
  const campaigns = section?.campaigns;
  if (!campaigns || typeof campaigns !== 'object' || Array.isArray(campaigns)) {
    return {};
  }
  const out: Record<string, CampaignUserLists> = {};
  for (const [campaignName, entry] of Object.entries(campaigns)) {
    const name = String(campaignName ?? '').trim();
    if (!name) {
      continue;
    }
    out[name] = {
      dummyUsers: extractDialerUsers(entry?.dummyUsers),
      activeUsers: extractDialerUsers(entry?.activeUsers),
    };
  }
  return out;
}

/**
 * Builds Cube campaign map from the cube section of activeConfig.
 * targetCampaign defaults to the campaign key when omitted (same-campaign transfer like Ozonetel).
 */
function buildCubeCampaigns(
  section: DialerCampaignsSection | undefined
): Record<string, CubeCampaignUserLists> {
  const campaigns = section?.campaigns;
  if (!campaigns || typeof campaigns !== 'object' || Array.isArray(campaigns)) {
    return {};
  }
  const out: Record<string, CubeCampaignUserLists> = {};
  for (const [campaignName, entry] of Object.entries(campaigns)) {
    const name = String(campaignName ?? '').trim();
    if (!name) {
      continue;
    }
    const targetCampaign = String(entry?.targetCampaign ?? name).trim() || name;
    out[name] = {
      dummyUsers: extractDialerUsers(entry?.dummyUsers),
      activeUsers: extractDialerUsers(entry?.activeUsers),
      targetCampaign,
    };
  }
  return out;
}

/**
 * Returns normalized Ozonetel campaigns for ozonetel-auto-transfer-config.ts → applyConfig().
 * Flow: CONNECTED call → match campaign → dummyUsers check → pick from activeUsers.
 */
export function getOzonetelCampaignsConfig(): { campaigns: Record<string, CampaignUserLists> } {
  return { campaigns: buildOzonetelCampaigns(activeConfig.ozonetel) };
}

/**
 * Returns normalized Cube campaigns for cube-auto-transfer-config.ts → applyConfig().
 * Flow: login campaign from remarks.cubeDetails → dummyUsers check → pick from activeUsers on targetCampaign.
 */
export function getCubeCampaignsConfig(): { campaigns: Record<string, CubeCampaignUserLists> } {
  return { campaigns: buildCubeCampaigns(activeConfig.cube) };
}

/**
 * Registers a callback to run when config is ready.
 * Local JSON: callback runs immediately on register.
 * S3 mode: callback runs after initAutoCallTransferConfig() fetch succeeds.
 */
export function onAutoCallTransferConfigReady(callback: ConfigReadyCallback): void {
  readyCallbacks.push(callback);
  if (!AUTO_CALL_TRANSFER_USE_S3) {
    callback();
  }
}

/**
 * Invokes all onAutoCallTransferConfigReady subscribers after activeConfig is set.
 */
function notifyReady(): void {
  for (const callback of readyCallbacks) {
    callback();
  }
}

/**
 * Loads auto-transfer config at app startup.
 *
 * Local (AUTO_CALL_TRANSFER_USE_S3 = false):
 *   activeConfig = bundled JSON → notifyReady() → dialer modules call get*CampaignsConfig().
 *
 * S3 (AUTO_CALL_TRANSFER_USE_S3 = true):
 *   fetch(AUTO_CALL_TRANSFER_S3_URL) → parse → activeConfig → notifyReady().
 *   Dialer modules re-apply config in initAutoCallTransferConfig().then(...) when flag is true.
 *
 * Fetch errors leave prior activeConfig unchanged; dialer auto-transfer stays off until config loads.
 */
export function initAutoCallTransferConfig(): Promise<void> {
  if (!AUTO_CALL_TRANSFER_USE_S3) {
    activeConfig = bundledConfig as AutoCallTransferConfig;
    notifyReady();
    return Promise.resolve();
  }
  return fetch(AUTO_CALL_TRANSFER_S3_URL)
    .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
    .then((raw) => {
      activeConfig = raw as AutoCallTransferConfig;
      notifyReady();
    })
    .catch(() => {});
}
